import { Component, Inject, OnInit, Optional } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { v4 as uuidv4 } from "uuid";
import { Subscription } from "rxjs";

import { AlertService } from "../../../shared/alert/service/alert.service";
import { LoaderService } from "../../../shared/loader/service/loader.service";
import { SharedModule } from "../../../shared/shared.module";
import { WorkHour } from "../../../services/generated/waba-api/models/work-hour";
import { WorkHourApiService } from "../../../services/generated/waba-api/services/work-hour-api.service";
import { GP_SECTION_ICONS } from "../../../features/administration/shared/gp-section-icons";

@Component({
  selector: "app-work-hour-popup",
  templateUrl: "./work-hour-popup.component.html",
  styleUrl: "./work-hour-popup.component.scss",
  standalone: true,
  imports: [SharedModule, MatDialogModule, ReactiveFormsModule],
})
export class WorkHourPopupComponent implements OnInit {
  workHourForm: FormGroup;
  workHourList: WorkHour[] = [];
  refPhoneCompany!: string;

  currentLang: string = "fr";
  isRTL: boolean = false;
  readonly headerIcon = GP_SECTION_ICONS.time;
  private langChangeSubscription?: Subscription;

  constructor(
      private fb: FormBuilder,
      private workHourApiService: WorkHourApiService,
      private alertService: AlertService,
      private translateService: TranslateService,
      private loaderService: LoaderService,
      @Optional() private dialogRef: MatDialogRef<WorkHourPopupComponent>,
      @Inject(MAT_DIALOG_DATA)
      public data: { refPhoneCompany: string },
  ) {
    this.workHourForm = this.fb.group({
      timeBlocks: this.fb.array([this.createTimeBlock()]),
    });
  }

  ngOnInit(): void {
    this.currentLang =
        this.translateService.currentLang ||
        sessionStorage.getItem("lang") ||
        "fr";

    this.translateService.use(this.currentLang);
    this.checkDirection();

    this.langChangeSubscription = this.translateService.onLangChange.subscribe(
        (event) => {
          this.currentLang = event.lang;
          this.checkDirection();
        },
    );

    if (this.data) {
      this.refPhoneCompany = this.data.refPhoneCompany;
      this.loadWorkHours();
    }
  }

  loadWorkHours() {
    this.loaderService.show();
    this.workHourApiService
        .listWorkHour({
          "x-api-requestId": uuidv4(),
          "x-api-canal": "web",
          refPhoneCompany: this.refPhoneCompany,
        })
        .subscribe((response) => {
          this.loaderService.hide();
          this.workHourList = response.body || [];

          if (this.workHourList.length > 0) {
            const timeBlockValues = this.workHourList.map((wh) => ({
              from: this.convertIntegerToTime(wh.startWorkHour ?? 0),
              to: this.convertIntegerToTime(wh.endWorkHour ?? 720),
              reference: wh.reference,
              days: [this.parseDays(wh.days)],
            }));

            this.workHourForm.setControl(
                "timeBlocks",
            this.fb.array(timeBlockValues.map((val) => this.fb.group(val))),
            );
          } else {
            this.createTimeBlock();
          }
        });
  }

  private checkDirection(): void {
    this.isRTL = this.currentLang === "ar";
  }

  private convertIntegerToTime(value: number): string {
    const hours = Math.floor(value / 100)
        .toString()
        .padStart(2, "0");
    const minutes = (value % 100).toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  get timeBlocks(): FormArray {
    return this.workHourForm?.get("timeBlocks") as FormArray;
  }

  createTimeBlock(): FormGroup {
    const lastToValue = this.timeBlocks?.at(-1)?.get("to")?.value;
    const lastDays: number[] = this.timeBlocks?.at(-1)?.get("days")?.value ?? [];
    return this.fb.group({
      reference: [null],
      from: [lastToValue || "00:00", Validators.required],
      to: [lastToValue || "12:00", Validators.required],
      days: [[...lastDays]],
    });
  }

  /** ISO days, 1 = Monday; none selected = every day. */
  readonly weekDays = [1, 2, 3, 4, 5, 6, 7];

  hasDay(block: any, day: number): boolean {
    return (block.get("days")?.value ?? []).includes(day);
  }

  toggleDay(block: any, day: number): void {
    const days: number[] = block.get("days")?.value ?? [];
    block.get("days")?.setValue(days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort());
  }

  private parseDays(days?: string | null): number[] {
    return (days ?? "")
        .split(",")
        .map((d) => Number(d.trim()))
        .filter((d) => d >= 1 && d <= 7);
  }

  /** "1,3,5"; null (every day) when none or all are selected, as waba stores it. */
  private daysText(days?: number[] | null): string | undefined {
    const unique = Array.from(new Set(days ?? [])).sort();
    return unique.length === 0 || unique.length === 7 ? undefined : unique.join(",");
  }

  addTimeBlock(): void {
    this.timeBlocks.push(this.createTimeBlock());
  }

  removeTimeBlock(index: number): void {
    if (this.timeBlocks.length > 1) {
      this.timeBlocks.removeAt(index);
    }
  }

  onSubmit(): void {
    if (this.workHourForm.invalid) {
      this.alertService.showError(
          this.translateService.instant("APP_GENERIC_ALERTS.REQUIRED_FIELDS"),
      );
      this.workHourForm.markAllAsTouched();
      return;
    }
    this.createOrUpdateWorkHours();
  }

  createOrUpdateWorkHours() {
    this.loaderService.show();
    const rawTimeBlocks = this.workHourForm.getRawValue().timeBlocks;

    const body: WorkHour[] = rawTimeBlocks.map((block: any) => ({
      reference: block.reference,
      startWorkHour: this.convertTimeToInteger(block.from),
      endWorkHour: this.convertTimeToInteger(block.to),
      days: this.daysText(block.days),
    }));

    this.workHourApiService
        .createOrUpdateListOfWorkHour({
          "x-api-canal": "web",
          "x-api-requestId": uuidv4(),
          refPhoneCompany: this.refPhoneCompany,
          body,
        })
        .subscribe({
          next: (response) => {
            this.loaderService.hide();
            if (response?.status === "200" && response.body) {
              this.alertService.showSuccess(
                  this.translateService.instant(
                      "APP_GENERIC_ALERTS.UPDATED_SUCCESS",
                  ),
              );
            } else {
              this.alertService.showError("" + response.message);
            }
            this.close();
          },
          error: () => {
            this.loaderService.hide();
            this.alertService.showError(
            this.translateService.instant("APP_GENERIC_ALERTS.UPDATE_FAILED"),
            );
          },
        });
  }

  private convertTimeToInteger(time: any): number {
    if (typeof time !== "string") {
      return 0;
    }
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 100 + minutes;
  }

  close(): void {
      this.dialogRef.close();
  }
}
